import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { GridAllModule } from '@syncfusion/ej2-angular-grids';
import { SkeletonModule } from '@syncfusion/ej2-angular-notifications';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MyAccountService } from '../account/my-account.service';
import { RequestErrorPolicyService } from '../error/request-error-policy.service';
import { ListingLayoutComponent } from '../shared/listing-layout/listing-layout.component';
import { CipaElectionHistoryItemDto } from './election-history.models';
import { ElectionHistoryService } from './election-history.service';

@Component({
  selector: 'app-election-history',
  standalone: true,
  imports: [
    CommonModule,
    GridAllModule,
    SkeletonModule,
    ButtonModule,
    ListingLayoutComponent,
  ],
  templateUrl: './election-history.component.html',
  styleUrls: ['./election-history.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElectionHistoryComponent implements OnInit, OnDestroy {
  isLoading = true;
  hasPasswordConfigured: boolean | null = null;
  elections: CipaElectionHistoryItemDto[] = [];

  pageSettings = { pageSize: 10, pageSizes: [10, 20, 50] };
  filterSettings = { type: 'Excel' };

  private readonly subs = new Subscription();

  constructor(
    private readonly electionHistoryService: ElectionHistoryService,
    private readonly myAccountService: MyAccountService,
    private readonly requestErrorPolicy: RequestErrorPolicyService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  goToMyAccount(): void {
    this.router.navigate(['/conta']);
  }

  getRandomWidth(min: number, max: number): string {
    const width = Math.floor(Math.random() * (max - min + 1)) + min;
    return `${width}px`;
  }

  get statusLabelMap(): Record<string, string> {
    return {
      rascunho: 'Rascunho',
      agendada: 'Agendada',
      votacao: 'Em votação',
      votacao_encerrada: 'Votação encerrada',
      encerrada: 'Encerrada',
      homologada: 'Homologada',
    };
  }

  getStatusLabel(status: string): string {
    return this.statusLabelMap[status] ?? status;
  }

  getStatusClass(status: string): string {
    return `status-${status.replace(/_/g, '-')}`;
  }

  openElectionResult(companySuffix: string, electionGuid: string): void {
    this.router.navigate(['/eleicoes', companySuffix, electionGuid]);
  }

  private loadData(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.myAccountService.getMyAccountProfile().pipe(
        switchMap((viewData) => {
          this.hasPasswordConfigured = viewData.profile.hasPasswordConfigured;
          if (!this.hasPasswordConfigured) {
            this.isLoading = false;
            this.cdr.markForCheck();
            throw null;
          }
          return this.electionHistoryService.getElectionHistory();
        }),
      ).subscribe({
        next: (elections) => {
          this.elections = elections;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          if (error === null) {
            return;
          }
          this.requestErrorPolicy.resolveNonHttpMessage(
            error,
            'Não foi possível carregar o histórico de eleições no momento.',
          );
          this.elections = [];
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      }),
    );
  }
}
